import {
  Create,
  SimpleForm,
  ReferenceInput,
  SelectInput,
  useGetOne,
  ResourceContextProvider,
  useRedirect,
} from 'react-admin';
import { useFormContext, useWatch } from 'react-hook-form';
import { useSearchParams, useLocation } from 'react-router-dom';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Breadcrumbs,
  Link,
  Typography,
  FormControl,
  FormLabel,
  FormControlLabel,
  Radio,
} from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { FormFields } from './FormFields';
import { ConditionalAIGenerator } from './AIFormGenerator';

const CREATION_CHOICES = [
  { id: 'blank', name: 'Blank new form' },
  { id: 'existing', name: 'Create from existing form' },
  { id: 'ai', name: 'Generate with AI (Beta)' },
];

function CreateBreadcrumbs() {
  const redirect = useRedirect();
  return (
    <Box sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 1, mb: 2 }}>
      <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />}>
        <Link
          underline="hover"
          component="button"
          variant="body1"
          onClick={() => redirect('/configuration')}
          sx={{ cursor: 'pointer' }}
        >
          Configuration
        </Link>
        <Link
          underline="hover"
          component="button"
          variant="body1"
          onClick={() => redirect('/configuration/forms')}
          sx={{ cursor: 'pointer' }}
        >
          Forms
        </Link>
        <Typography color="text.primary">Create</Typography>
      </Breadcrumbs>
    </Box>
  );
}

function CloneAndBaseFormSync() {
  const { setValue, reset } = useFormContext();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const cloneFromId = searchParams.get('cloneFrom');
  const sourceParam = searchParams.get('source');
  const recordFromState = (location.state as { record?: Record<string, unknown> } | null)
    ?.record;
  let sourceFromParam: Record<string, unknown> | null = null;
  if (sourceParam) {
    try {
      sourceFromParam = JSON.parse(decodeURIComponent(sourceParam));
    } catch {
      /* ignore */
    }
  }
  const sourceFormId =
    cloneFromId ||
    (recordFromState?.id != null ? String(recordFromState.id) : null);
  const { data: fetchedClone } = useGetOne(
    'forms',
    { id: sourceFormId! },
    { enabled: !!sourceFormId && !recordFromState && !sourceFromParam },
  );
  const formToClone =
    recordFromState ||
    sourceFromParam ||
    fetchedClone ||
    null;

  const [cloneApplied, setCloneApplied] = useState(false);
  useEffect(() => {
    if (!formToClone?.id || cloneApplied) return;
    const fd =
      typeof formToClone.formDefinition === 'string'
        ? formToClone.formDefinition
        : JSON.stringify(formToClone.formDefinition ?? { fields: [] });
    reset(
      {
        formCreationType: 'existing',
        baseFormId: formToClone.id,
        name: `${formToClone.name} (Copy)`,
        formDefinition: fd,
        emails: formToClone.emails ?? '',
        notify: formToClone.notify ?? false,
        active: true,
      },
      { keepDefaultValues: false },
    );
    setCloneApplied(true);
  }, [formToClone, reset, cloneApplied]);

  const formCreationType = useWatch({ name: 'formCreationType' });
  const baseFormId = useWatch({ name: 'baseFormId' });
  const lastPopulatedRef = useRef<number | null>(null);

  const { data: selectedForm } = useGetOne(
    'forms',
    { id: baseFormId! },
    {
      enabled:
        !!baseFormId &&
        formCreationType === 'existing' &&
        !cloneFromId &&
        !recordFromState &&
        !sourceParam,
    },
  );

  useEffect(() => {
    if (formCreationType !== 'existing' || !baseFormId || !selectedForm) return;
    if (lastPopulatedRef.current === baseFormId) return;
    const fd =
      typeof selectedForm.formDefinition === 'string'
        ? selectedForm.formDefinition
        : JSON.stringify(selectedForm.formDefinition ?? { fields: [] });
    setValue('name', `${selectedForm.name} (Copy)`, { shouldDirty: true });
    setValue('formDefinition', fd, { shouldDirty: true });
    setValue('emails', selectedForm.emails ?? '', { shouldDirty: true });
    setValue('notify', selectedForm.notify ?? false, { shouldDirty: true });
    setValue('active', true, { shouldDirty: true });
    lastPopulatedRef.current = baseFormId;
  }, [formCreationType, baseFormId, selectedForm, setValue]);

  useEffect(() => {
    if (formCreationType === 'blank') {
      lastPopulatedRef.current = null;
    }
  }, [formCreationType]);

  return null;
}

function AlertLike({ children }: { children: React.ReactNode }) {
  return (
    <Box sx={{ mb: 2, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
      <Typography variant="body2">{children}</Typography>
    </Box>
  );
}

function CreationModeFields() {
  const { setValue } = useFormContext();
  const formCreationType = useWatch({ name: 'formCreationType' });
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const cloneFromId = searchParams.get('cloneFrom');
  const sourceParam = searchParams.get('source');
  const recordFromState = (location.state as { record?: { id?: number } } | null)
    ?.record;
  const isCloning = !!(
    cloneFromId ||
    (recordFromState && recordFromState.id) ||
    sourceParam
  );

  if (isCloning) {
    return (
      <AlertLike>
        Cloning an existing form. Adjust the name and definition, then save.
      </AlertLike>
    );
  }

  return (
    <FormControl component="fieldset" variant="standard" sx={{ mb: 2, width: '100%' }}>
      <FormLabel component="legend" sx={{ mb: 1 }}>
        How do you want to start?
      </FormLabel>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'wrap',
          alignItems: 'flex-start',
          gap: 2,
        }}
      >
        {CREATION_CHOICES.map((choice) => (
          <FormControlLabel
            key={choice.id}
            control={
              <Radio
                color="primary"
                checked={formCreationType === choice.id}
                onChange={() =>
                  setValue('formCreationType', choice.id, { shouldDirty: true })
                }
                value={choice.id}
              />
            }
            label={choice.name}
          />
        ))}
      </Box>
      <FormCreationTypeExtras />
    </FormControl>
  );
}

function FormCreationTypeExtras() {
  const formCreationType = useWatch({ name: 'formCreationType' });
  if (formCreationType !== 'existing') return null;
  return (
    <Box sx={{ maxWidth: 480, mb: 2 }}>
      <ReferenceInput
        source="baseFormId"
        reference="forms"
        label="Base form"
        sort={{ field: 'name', order: 'ASC' }}
        perPage={500}
        filter={{ active: true }}
      >
        <SelectInput optionText="name" helperText="Start from a copy of this template" />
      </ReferenceInput>
    </Box>
  );
}

export const FormsCreate = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const cloneFromId = searchParams.get('cloneFrom');
  const sourceParam = searchParams.get('source');
  const recordFromState = (location.state as { record?: { id?: number } } | null)
    ?.record;
  const isCloning = !!(
    cloneFromId ||
    (recordFromState && recordFromState.id) ||
    sourceParam
  );

  const defaultValues = useMemo(
    () =>
      isCloning
        ? {
            formCreationType: 'existing',
            active: true,
            formDefinition: JSON.stringify({ fields: [] }),
          }
        : {
            formCreationType: 'blank',
            active: true,
            formDefinition: JSON.stringify({ fields: [] }),
          },
    [isCloning],
  );

  const transform = (data: Record<string, unknown>) => {
    const { baseFormId, formCreationType: _t, ...rest } = data;
    if (rest.formDefinition && typeof rest.formDefinition !== 'string') {
      rest.formDefinition = JSON.stringify(rest.formDefinition);
    }
    return rest;
  };

  return (
    <ResourceContextProvider value="forms">
      <Create
        title="Create form template"
        redirect={() => 'configuration/forms'}
        transform={transform}
      >
        <SimpleForm defaultValues={defaultValues}>
          <CreateBreadcrumbs />
          <CloneAndBaseFormSync />
          <CreationModeFields />
          <ConditionalAIGenerator />
          <FormFields />
        </SimpleForm>
      </Create>
    </ResourceContextProvider>
  );
};
